import React, { useState, useEffect } from 'react';
import { ContextLoader } from './components/ContextLoader';
import { BacklogDisplay } from './components/BacklogDisplay';
import { AzureDevOpsConfigManager } from './components/AzureDevOpsConfigManager';
import { AzureDevOpsPublisher } from './components/AzureDevOpsPublisher';
import InstructionsBuilder from './components/InstructionsBuilder';
import { ProjectManager, Project } from './components/ProjectManager';
import TeamsManager from './components/TeamsManager';
import { Button, Alert } from './components/UIComponents';
import { generateBacklogWithContext, checkHealth, downloadExcel, ExportExcelRequest, configureAuthInterceptor, setCurrentUserEmail, updateSharedProject } from './services/api';
import { ParsedBacklog } from './types/story';
import { useAuth, UserHeader } from './auth';
import './index.css';



function App() {
  const { getAccessToken, user, isSuperAdmin } = useAuth();
  const [showTeamsManager, setShowTeamsManager] = useState(false);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [contextPath, setContextPath] = useState<string>('');
  const [additionalInstructions, setAdditionalInstructions] = useState<string>('');
  const [backlog, setBacklog] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; message: string } | null>(null);
  const [healthStatus, setHealthStatus] = useState<'unknown' | 'healthy' | 'unhealthy'>('unknown');
  const [selectedAzureConfig, setSelectedAzureConfig] = useState<string>('');
  const [showAzureConfig, setShowAzureConfig] = useState(false);

  // Configurar el interceptor de autenticación con la función de obtención de token
  useEffect(() => {
    configureAuthInterceptor(getAccessToken);
  }, [getAccessToken]);

  // Propagar el email del usuario al interceptor de API
  useEffect(() => {
    setCurrentUserEmail(user?.email || '');
  }, [user]);

  useEffect(() => {
    // Check API health on mount
    const checkApiHealth = async () => {
      try {
        const response = await checkHealth();
        setHealthStatus(response.agent_initialized ? 'healthy' : 'unhealthy');
      } catch (err) {
        setHealthStatus('unhealthy');
      }
    };
    
    checkApiHealth();
  }, []);

  // Auto-guardar proyecto cuando cambia el backlog
  useEffect(() => {
    if (currentProject && backlog) {
      const updatedProject = {
        ...currentProject,
        backlog,
        contextPath,
        instructions: additionalInstructions,
        updatedAt: new Date().toISOString()
      };
      setCurrentProject(updatedProject);
      // Persist to DB (non-blocking)
      if (currentProject.id) {
        updateSharedProject(currentProject.id, {
          backlog,
          context_path: contextPath,
          instructions: additionalInstructions,
        }).catch(e => console.warn('Error saving project to DB:', e));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backlog]);

  // Handlers de proyecto
  const handleProjectSelect = (project: Project) => {
    setCurrentProject(project);
    setContextPath(project.contextPath || '');
    setAdditionalInstructions(project.instructions || '');
    setBacklog(project.backlog || null);
    setAlert({
      type: 'info',
      message: `Proyecto "${project.name}" cargado`
    });
  };

  const handleProjectCreate = (project: Project) => {
    setCurrentProject(project);
    setContextPath('');
    setAdditionalInstructions('');
    setBacklog(null);
    setAlert({
      type: 'success',
      message: `Proyecto "${project.name}" creado`
    });
  };

  const handleProjectUpdate = (project: Project) => {
    setCurrentProject(project);
  };

  const handleProjectDelete = (projectId: string) => {
    if (currentProject?.id === projectId) {
      setCurrentProject(null);
      setContextPath('');
      setAdditionalInstructions('');
      setBacklog(null);
    }
  };

  const handleGenerateBacklog = async () => {
    if (!contextPath && !additionalInstructions) {
      setAlert({
        type: 'warning',
        message: 'Por favor carga un directorio de contexto o proporciona instrucciones adicionales.'
      });
      return;
    }

    setLoading(true);
    setError(null);
    setBacklog(null);
    setAlert(null);

    try {
      // Usar Deep Analysis endpoint (análisis exhaustivo sin MongoDB)
      const response = await generateBacklogWithContext({
        feature_description: contextPath || '', // directory_path
        repository_name: contextPath ? contextPath.split('/').pop() : undefined,
        additional_instructions: additionalInstructions || undefined,
        include_standard_tasks: true,
        max_context_tokens: 4000
      });

      if (response.success && response.backlog) {
        setBacklog(response.backlog);
        setAlert({
          type: 'success',
          message: '✓ Backlog generado exitosamente con análisis exhaustivo'
        });
      } else {
        setError(response.error || 'Error desconocido al generar el backlog');
        setAlert({
          type: 'error',
          message: 'Error al generar el backlog. Revisa los detalles abajo.'
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error de conexión con el backend';
      setError(errorMessage);
      setAlert({
        type: 'error',
        message: 'No se pudo conectar con el servicio. Verifica que el backend esté ejecutándose.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClearAll = () => {
    setContextPath('');
    setAdditionalInstructions('');
    setBacklog(null);
    setError(null);
    setAlert(null);
  };

  const handleExportExcel = async (parsedBacklog: ParsedBacklog) => {
    try {
      const request: ExportExcelRequest = {
        stories: parsedBacklog.stories.map(story => ({
          id: story.id,
          title: story.title,
          user_story: story.user_story,
          rol: story.rol,
          criterios_funcionales: story.criterios_funcionales,
          criterios_tecnicos: story.criterios_tecnicos,
          figma_links: story.figma_links,
          insumos: story.insumos,
          story_points: story.story_points,
          priority: story.priority,
          epic: story.epic,
          status: story.status,
          tasks: story.tasks.map(t => ({
            title: t.title,
            description: t.description,
            type: t.type,
            estimated_hours: t.estimated_hours
          }))
        })),
        executive_summary: parsedBacklog.executive_summary
      };

      await downloadExcel(request);
      
      setAlert({
        type: 'success',
        message: '✓ Archivo Excel descargado exitosamente'
      });
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      setAlert({
        type: 'error',
        message: 'Error al exportar a Excel. Verifica la conexión con el backend.'
      });
    }
  };

  const handleBacklogUpdate = (newMarkdown: string) => {
    setBacklog(newMarkdown);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gft-light via-white to-gray-50">
      {/* Header */}
      <header className="bg-gft-dark text-white shadow-xl">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex items-center gap-4 mr-6">
                <img src="/gft-logo.png" alt="GFT Logo" className="h-12" />
                <div className="h-10 w-px bg-gray-500"></div>
                <img src="/wynxx-logo-sin-fondo.png" alt="Wynxx Logo" className="h-12" />
              </div>
              <div>
                <h1 className="text-3xl font-bold flex items-center">
                  <span className="text-gft-blue mr-3">🚀</span>
                  Backlog Generator Accelerator
                </h1>
                <p className="mt-2 text-gray-300 text-sm">
                  Generador de Historias de Usuario con IA
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <span 
                  className={`h-3 w-3 rounded-full mr-2 ${
                    healthStatus === 'healthy' ? 'bg-green-400' : 
                    healthStatus === 'unhealthy' ? 'bg-red-400' : 
                    'bg-yellow-400'
                  }`}
                />
                <span className="text-sm">
                  {healthStatus === 'healthy' ? 'API Conectada' : 
                   healthStatus === 'unhealthy' ? 'API Desconectada' : 
                   'Verificando...'}
                </span>
              </div>
              {/* Teams Manager button (superadmin only) */}
              {isSuperAdmin && (
                <button
                  onClick={() => setShowTeamsManager(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-700 hover:bg-purple-600 text-white rounded-lg text-sm font-medium transition-colors"
                  title="Gestionar equipos"
                >
                  👥 Teams
                </button>
              )}
              {/* User Authentication Header */}
              <UserHeader className="ml-4" />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Alert */}
        {alert && (
          <div className="mb-6">
            <Alert
              type={alert.type}
              message={alert.message}
              onClose={() => setAlert(null)}
            />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Input Controls */}
          <div className="lg:col-span-1 space-y-4">
            {/* Project Manager */}
            <ProjectManager
              currentProject={currentProject}
              onProjectSelect={handleProjectSelect}
              onProjectCreate={handleProjectCreate}
              onProjectUpdate={handleProjectUpdate}
              onProjectDelete={handleProjectDelete}
              isSuperAdmin={isSuperAdmin}
            />

            {/* Context Loader */}
            <ContextLoader
              onPathSelected={setContextPath}
              currentPath={contextPath}
            />

            {/* Instructions Builder - Selector de roles y opciones */}
            <InstructionsBuilder
              onInstructionsChange={setAdditionalInstructions}
              initialInstructions={additionalInstructions}
            />

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                onClick={handleGenerateBacklog}
                loading={loading}
                disabled={loading || healthStatus === 'unhealthy'}
                className="w-full"
                variant="primary"
              >
                {loading ? 'Generando...' : '✨ Generar Backlog'}
              </Button>

              <Button
                onClick={handleClearAll}
                disabled={loading}
                className="w-full"
                variant="outline"
              >
                🗑️ Limpiar Todo
              </Button>
              
              <Button
                onClick={() => setShowAzureConfig(!showAzureConfig)}
                className="w-full"
                variant="outline"
              >
                {showAzureConfig ? '🔼 Ocultar' : '⚙️ Configurar'} Azure DevOps
              </Button>
            </div>

            {/* Azure DevOps Configuration */}
            {showAzureConfig && (
              <AzureDevOpsConfigManager
                onConfigSelected={setSelectedAzureConfig}
                selectedConfigId={selectedAzureConfig}
              />
            )}

            {/* Info Card */}
            <div className="bg-gradient-to-br from-gft-purple to-gft-dark text-white rounded-xl shadow-lg p-6">
              <h4 className="font-bold text-lg mb-3">📖 Especificaciones</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>Formato: Como/Quiero/Para</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>Regla INVEST completa</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>Criterios de aceptación verificables</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>Taxonomía Azure DevOps</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>Priorización por valor de negocio</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">🚀</span>
                  <span>Integración con Azure DevOps Boards</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Right Column - Backlog Display and Azure Publishing */}
          <div className="lg:col-span-2 space-y-6">
            <BacklogDisplay
              backlog={backlog}
              loading={loading}
              error={error}
              onBacklogUpdate={handleBacklogUpdate}
              onExportExcel={handleExportExcel}
            />
            
            {backlog && (
              <AzureDevOpsPublisher
                backlog={backlog}
                selectedConfigId={selectedAzureConfig}
              />
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gft-dark text-white mt-12 py-6">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm text-gray-300">
            AWS Strands SDK + Amazon Bedrock
          </p>
          <p className="text-xs text-gray-400 mt-2">
            © {new Date().getFullYear()} GFT Colombia - Santiago Aponte Marin - sopp@gft.com - Backlog Generator Accelerator
          </p>
        </div>
      </footer>

      {/* Teams Manager overlay */}
      {showTeamsManager && (
        <TeamsManager onClose={() => setShowTeamsManager(false)} />
      )}
    </div>
  );
}

export default App;
